import { Chip, Stack, Typography } from '@mui/material';
import { ConversationStatus } from '@provision/conversation-types';
import { useCallback, useMemo } from 'react';
import { SiteRelatedTabs } from 'src/components/SidePanelTabs/SiteTab/site-related-tabs';
import { SiteTab } from 'src/components/SidePanelTabs/SiteTab/site-tab';
import { SiteMenu } from 'src/components/SiteComponents/site-menu';
import { SiteShareButton } from 'src/components/SiteComponents/site-share-button';
import { Address, Conversation } from 'src/graphql';
import { useUpdateAssociatedSite } from 'src/pages/Sites/use-update-associated-site';
import useConversationViewsStore from 'src/store/conversation-views';
import { AssociatedSiteFormValues, useAssociatedSiteForm } from './use-associated-site-form';

export const ConversationSite = ({
  conversation,
  address,
  variant = 'standard',
  associatedAddressesCount,
}: {
  conversation?: Conversation;
  address?: Partial<Address>;
  variant?: 'standard' | 'scrollable' | 'fullWidth';
  associatedAddressesCount?: number;
}) => {
  const { associatedAddressesPlaceholders } = useConversationViewsStore();

  const currentAddress = useMemo(
    () =>
      (address ??
        conversation?.address ??
        associatedAddressesPlaceholders[conversation?.conversationId!]?.[0]) as Address,
    [
      address,
      associatedAddressesPlaceholders,
      conversation?.address,
      conversation?.conversationId,
    ],
  );

  const disabled =
    (conversation?.conversationStatusId as ConversationStatus) === ConversationStatus.Closed ||
    !!conversation?.isDeleted;

  const updateAssociatedSite = useUpdateAssociatedSite(
    currentAddress,
    conversation?.conversationId,
  );

  const { associatedSiteFormFields } = useAssociatedSiteForm(currentAddress, disabled);

  const placeholderAddresses =
    associatedAddressesPlaceholders[conversation?.conversationId ?? ''] ?? [];
  const derivedAssociatedAddressesCount =
    associatedAddressesCount ?? Math.max(placeholderAddresses.length, currentAddress ? 1 : 0);
  const showMultipleSitesIndicator = derivedAssociatedAddressesCount > 1;
  const additionalSitesCount = Math.max(derivedAssociatedAddressesCount - 1, 0);

  const onSubmitAssociatedSite = useCallback(
    (values: AssociatedSiteFormValues) => {
      void updateAssociatedSite(values);
    },
    [updateAssociatedSite],
  );

  return (
    <SiteTab
      title={
        conversation?.conversationId ? (
          <Stack direction="row" spacing={1} alignItems="center" paddingBottom={1}>
            <Typography variant="subtitle2" fontSize="0.75rem">
              Associated Site
            </Typography>
            {showMultipleSitesIndicator ? (
              <Chip
                size="small"
                variant="outlined"
                label={`+${additionalSitesCount} more available sites`}
                sx={{
                  borderRadius: 999,
                  borderColor: 'divider',
                  color: 'text.secondary',
                  fontSize: '0.6875rem',
                  fontWeight: 500,
                  height: 20,
                }}
              />
            ) : null}
          </Stack>
        ) : null
      }
      address={currentAddress}
      onSubmit={onSubmitAssociatedSite}
      fields={associatedSiteFormFields}
      addressRightAdornment={
        <>
          <SiteShareButton
            address={currentAddress}
            partnerId={currentAddress?.partner?.partnerId ?? currentAddress?.partnerId}
          />
          <SiteMenu address={currentAddress} conversation={conversation} />
        </>
      }
    >
      <SiteRelatedTabs address={currentAddress} variant={variant} />
    </SiteTab>
  );
};
